import { Fragment, useMemo, useState } from 'react';
import { dateTime, num } from '../lib/format.js';
import { EmptyState, StatusBadge } from '../components/ui.js';
import '../screens-2c.css';

export interface ListRow {
  extId: string;
  name: string;
  publicName: string | null;
  senderName: string | null;
  senderEmail: string | null;
  replyTo: string | null;
  signature: string | null;
  notes: string | null;
  created: string | null;
  category: string | null;
  hidden: boolean | null;
  openRate: number | null;
  clickRate: number | null;
  segmentId: number | null;
  /** Počty ze SE (`GET /contactlists/{id}/distribution`); `null` = zatím nenačteno. */
  total: number | null;
  confirmedTotal: number | null;
  unsubscribedTotal: number | null;
  countsAt: string | null;
  /** Kolik členů už je v zrcadle hubu a kolik z nich je spárovaných s kontaktem v hubu. */
  mirrored: number;
  inHub: number;
}

export interface Selection {
  listIds: number[];
  consentListIds: number[];
}

type SortKey = 'name' | 'id' | 'total' | 'confirmed' | 'unsubscribed' | 'mirrored';

const sortValue = (l: ListRow, key: SortKey): string | number => {
  switch (key) {
    case 'name':
      return l.name.toLocaleLowerCase('cs');
    case 'id':
      return Number(l.extId);
    case 'total':
      return l.total ?? -1;
    case 'confirmed':
      return l.confirmedTotal ?? -1;
    case 'unsubscribed':
      return l.unsubscribedTotal ?? -1;
    case 'mirrored':
      return l.mirrored;
  }
};

const seznamy = (n: number): string =>
  n === 1 ? 'seznam' : n >= 2 && n <= 4 ? 'seznamy' : 'seznamů';

const dash = (v: number | null): string => (v === null ? '—' : num(v));

/** Tabulka seznamů ze SmartEmailingu s výběrem pro import a pro doložení souhlasu. */
export function SeLists({
  lists,
  selection,
  onChange,
  canWrite,
  tz,
}: {
  lists: ListRow[];
  selection: Selection;
  onChange: (next: Selection) => void;
  canWrite: boolean;
  tz: string;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [detail, setDetail] = useState<string | null>(null);

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('cs');
    const rows = needle
      ? lists.filter((l) =>
          [l.name, l.publicName, l.extId, l.senderName, l.senderEmail, l.category].some((v) =>
            (v ?? '').toLocaleLowerCase('cs').includes(needle),
          ),
        )
      : lists;
    return [...rows].sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
    });
  }, [lists, search, sort]);

  const importSet = new Set(selection.listIds);
  const consentSet = new Set(selection.consentListIds);
  const setImport = (id: number, on: boolean): void =>
    onChange({
      listIds: on
        ? [...new Set([...selection.listIds, id])]
        : selection.listIds.filter((x) => x !== id),
      // souhlas bez importu seznamu nedává smysl
      consentListIds: on
        ? selection.consentListIds
        : selection.consentListIds.filter((x) => x !== id),
    });
  const setConsent = (id: number, on: boolean): void =>
    onChange({
      listIds: on ? [...new Set([...selection.listIds, id])] : selection.listIds,
      consentListIds: on
        ? [...new Set([...selection.consentListIds, id])]
        : selection.consentListIds.filter((x) => x !== id),
    });
  const ids = visible.map((l) => Number(l.extId));
  const th = (key: SortKey, text: string, numeric = false): JSX.Element => (
    <th
      className={numeric ? 'n' : undefined}
      aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
    >
      <button
        className="linkbtn"
        onClick={() => setSort({ key, dir: sort.key === key ? (sort.dir === 1 ? -1 : 1) : 1 })}
      >
        {text}
        {sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );

  const chosen = lists.filter((l) => importSet.has(Number(l.extId)));
  const consentChosen = lists.filter((l) => consentSet.has(Number(l.extId)));
  const sum = (rows: ListRow[], pick: (l: ListRow) => number | null): number =>
    rows.reduce((a, l) => a + (pick(l) ?? 0), 0);
  const unknown = chosen.filter((l) => l.total === null).length;

  return (
    <>
      <div className="row">
        <input
          type="search"
          value={search}
          placeholder="hledat seznam: název, číslo, odesílatel, kategorie"
          className="s2c-search"
          aria-label="Hledat seznam"
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="muted">
          Zobrazeno {num(visible.length)} z {num(lists.length)} seznamů
        </span>
        {canWrite ? (
          <>
            <button
              className="btn"
              onClick={() =>
                onChange({
                  ...selection,
                  listIds: [...new Set([...selection.listIds, ...ids])],
                })
              }
            >
              Vybrat vše zobrazené
            </button>
            <button
              className="btn"
              onClick={() =>
                onChange({
                  listIds: selection.listIds.filter((x) => !ids.includes(x)),
                  consentListIds: selection.consentListIds.filter((x) => !ids.includes(x)),
                })
              }
            >
              Nevybírat nic z zobrazených
            </button>
          </>
        ) : null}
      </div>
      <div className="help" role="note">
        <p>
          <b>Sloupec „Potvrzení členové dokládají souhlas“.</b>
          Zaškrtněte jen seznam, u kterého členství opravdu znamená marketingový souhlas (například
          dvojí potvrzení přihlášení). Potvrzení členové takového seznamu dostanou v hubu souhlas s
          marketingem. Odhlášení a blokovaní souhlas nikdy nedostanou. Bez zaškrtnutí se nikomu
          souhlas neudělí a importované kontakty marketing nedostanou.
        </p>
      </div>
      <div className="scroll">
        <table data-testid="se-lists-table">
          <thead>
            <tr>
              <th className="s2c-thwrap">Importovat kontakty</th>
              <th className="s2c-thwrap">Potvrzení členové dokládají souhlas</th>
              {th('name', 'Seznam')}
              {th('id', 'Číslo', true)}
              {th('total', 'Celkem', true)}
              {th('confirmed', 'Potvrzených', true)}
              {th('unsubscribed', 'Odhlášených', true)}
              <th>Odesílatel</th>
              <th>Kategorie</th>
              {th('mirrored', 'V hubu', true)}
            </tr>
          </thead>
          <tbody>
            {visible.map((l) => {
              const id = Number(l.extId);
              const open = detail === l.extId;
              return (
                <Fragment key={l.extId}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Importovat kontakty ze seznamu ${l.name}`}
                        checked={importSet.has(id)}
                        disabled={!canWrite}
                        onChange={(e) => setImport(id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Potvrzení členové seznamu ${l.name} dokládají marketingový souhlas`}
                        checked={consentSet.has(id)}
                        disabled={!canWrite}
                        onChange={(e) => setConsent(id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <button
                        className="linkbtn"
                        aria-expanded={open}
                        onClick={() => setDetail(open ? null : l.extId)}
                      >
                        {l.name}
                      </button>
                      {l.publicName && l.publicName !== l.name ? (
                        <span className="muted"> · veřejně „{l.publicName}“</span>
                      ) : null}
                      {l.hidden ? <StatusBadge tone="neu">skrytý</StatusBadge> : null}
                    </td>
                    <td className="n">
                      <code>{l.extId}</code>
                    </td>
                    <td className="n">{dash(l.total)}</td>
                    <td className="n">{dash(l.confirmedTotal)}</td>
                    <td className="n">{dash(l.unsubscribedTotal)}</td>
                    <td>{l.senderName ?? l.senderEmail ?? '—'}</td>
                    <td>{l.category ?? '—'}</td>
                    <td className="n">
                      {l.mirrored > 0
                        ? `už načteno ${num(l.mirrored)}${l.inHub ? ` (v hubu ${num(l.inHub)})` : ''}`
                        : 'nic'}
                    </td>
                  </tr>
                  {open ? (
                    <tr>
                      <td colSpan={10}>
                        <dl className="kv">
                          <dt>Název</dt>
                          <dd>{l.name}</dd>
                          <dt>Veřejný název</dt>
                          <dd>{l.publicName ?? '—'}</dd>
                          <dt>Odesílatel</dt>
                          <dd>
                            {l.senderName ?? '—'}
                            {l.senderEmail ? ` <${l.senderEmail}>` : ''}
                          </dd>
                          <dt>Odpovědi na</dt>
                          <dd>{l.replyTo ?? '—'}</dd>
                          <dt>Podpis</dt>
                          <dd>{l.signature ?? '—'}</dd>
                          <dt>Založen</dt>
                          <dd>{l.created ?? '—'}</dd>
                          <dt>Kategorie</dt>
                          <dd>{l.category ?? '—'}</dd>
                          <dt>Poznámka</dt>
                          <dd>{l.notes ?? '—'}</dd>
                          <dt>Otevření / kliknutí</dt>
                          <dd>
                            {l.openRate === null ? '—' : `${l.openRate} %`} /{' '}
                            {l.clickRate === null ? '—' : `${l.clickRate} %`}
                          </dd>
                          <dt>Segment</dt>
                          <dd>{l.segmentId ?? '—'}</dd>
                          <dt>Počty načteny</dt>
                          <dd>{l.countsAt ? dateTime(l.countsAt, tz) : 'zatím ne'}</dd>
                        </dl>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {visible.length === 0 ? (
        <EmptyState title="Žádný seznam neodpovídá">Zkuste jiný hledaný výraz.</EmptyState>
      ) : null}

      <div data-testid="se-selection-summary">
        <h3>
          Vybráno {num(chosen.length)} {seznamy(chosen.length)}
          {chosen.length > 0 && unknown < chosen.length
            ? `, ~${num(sum(chosen, (l) => l.total))} kontaktů`
            : chosen.length > 0
              ? ' (počty členů zatím nejsou načteny)'
              : ''}
        </h3>
        <p className="note">
          Součet členů vybraných seznamů je horní odhad: kontakt, který je ve více seznamech, se do
          importu počítá jednou.
          {unknown > 0
            ? ` U ${num(unknown)} vybraných seznamů počet zatím nebyl načten (v součtu chybí).`
            : ''}{' '}
          Seznamy se souhlasem: {num(consentChosen.length)}, potvrzených členů až ~
          {num(sum(consentChosen, (l) => l.confirmedTotal))}.
        </p>
        {chosen.length > 0 ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Vybraný seznam</th>
                  <th className="n">Celkem</th>
                  <th className="n">Potvrzených</th>
                  <th className="n">Odhlášených</th>
                  <th>Souhlas</th>
                </tr>
              </thead>
              <tbody>
                {chosen.slice(0, 15).map((l) => (
                  <tr key={l.extId}>
                    <td>
                      {l.name} <span className="muted">· {l.extId}</span>
                    </td>
                    <td className="n">{dash(l.total)}</td>
                    <td className="n">{dash(l.confirmedTotal)}</td>
                    <td className="n">{dash(l.unsubscribedTotal)}</td>
                    <td>
                      {consentSet.has(Number(l.extId)) ? (
                        <StatusBadge tone="warn" icon="alert">
                          potvrzení členové dostanou souhlas
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="neu">bez souhlasu</StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {chosen.length > 15 ? (
              <p className="note">… a dalších {num(chosen.length - 15)} seznamů.</p>
            ) : null}
          </div>
        ) : (
          <p className="note" role="status">
            Zatím není vybraný žádný seznam. Zaškrtněte „Importovat kontakty“ u seznamů, které
            chcete.
          </p>
        )}
      </div>
    </>
  );
}
